/**
 * Points of Contact (POC) helpers: fields, default values, validation rules
 * and saved-data mapping for one POC group, built from `pocFieldConfigs`.
 */
import { isValidValue } from "utils";
import { pocFieldConfigs } from "./pocFieldConfig";
import { combineRules, getPath, rhfRules } from "../../rhf/rules";

// Field list of one POC group (order included)
export const pocFields = (role, arr = []) => {
  const fields = [];
  arr.forEach((e) => {
    const temp = { ...pocFieldConfigs[e] };
    if (e === "role") {
      if (role === "other") temp.defaultValue = false;
      else temp.value = role;
    }
    if (e === "first_name_prefix") fields.push(pocFieldConfigs["first_name"]);
    if (e === "phone") fields.push(pocFieldConfigs["countryCode"]);
    fields.push(temp);
  });
  return fields;
};

/** Default values for one POC group: `{ type, ...fields }`. */
export const buildPocDefaults = (role, arr = []) => {
  const values = { type: role };
  pocFields(role, arr).forEach((f) => {
    values[f.label] = f.value;
  });
  return values;
};

// A field counts as filled when it has a non-empty value
const isFilled = (v) => isValidValue(v) && v !== "";

/** Labels of the fields an optional POC watches ("fill one → fill all"). */
export const pocWatchedFields = (role, arr = []) =>
  pocFields(role, arr)
    .filter((f) => !f.defaultValue)
    .map((f) => f.label);

/**
 * Rules for one POC group at `path` (e.g. "principal" or "other.*").
 * - status true (required POC): every field without `defaultValue` is required
 * - status false (optional POC): once any watched field has a value, the other
 *   watched fields become required
 */
export const buildPocRules = (path, role, status = false, arr = []) => {
  const fields = pocFields(role, arr);
  const watched = pocWatchedFields(role, arr);
  const map = {};
  fields.forEach((f) => {
    let rules = combineRules(f.rules || null);
    if (status && !f.defaultValue) rules = combineRules(rules, rhfRules.required);
    if (!status && !f.defaultValue) {
      rules = combineRules(rules, {
        validate: {
          required: (value, values, fieldPath) => {
            const group = getPath(values, fieldPath.split(".").slice(0, -1).join("."));
            const hasAnyValue = watched.some((label) => isFilled(group?.[label]));
            return !(hasAnyValue && !isFilled(value));
          },
        },
      });
    }
    map[`${path}.${f.label}`] = rules;
  });
  return map;
};

/**
 * Map saved POC data onto a POC group's values: only keys that already exist
 * in the group are set, empty values and phone numbers containing "null" are
 * skipped, custom_role is ignored.
 */
export const pocDataToValues = (defaults, poc, phoneKey = "phone_number") => {
  const values = { ...defaults };
  Object.keys(poc).forEach((el) => {
    if (!isValidValue(poc[el])) return;
    if (el === "custom_role") return;
    if (el === phoneKey && String(poc[el]).includes("null")) return;
    if (el in values) values[el] = poc[el];
  });
  return values;
};
